import { useMemo } from 'react'
import useBedbanksRates from './useBedbanksRates'
import type { RoomDetails } from 'lib/search/searchUtils'
import { excludeNullOrUndefined } from 'checkout/utils'

interface BedbankRoom {
  rateId: string
  propertyId: string
}

function useBedbankRoomBreakdown(
  bedbankRooms: Array<BedbankRoom>,
  filters?: App.OfferListFilters,
): Array<RoomDetails> {
  const bedbankPropertyIds = useMemo(() =>
    bedbankRooms.map(r => r.propertyId),
  [bedbankRooms])

  const [ratesMap, fetchingMap] = useBedbanksRates(
    bedbankPropertyIds,
    filters?.rooms,
    filters?.checkIn,
    filters?.checkOut,
    bedbankPropertyIds.length > 0
  )

  const bedbankRoomsList = useMemo((): Array<RoomDetails> => {
    // Group bedbank rooms by room type and count
    const roomDetails = bedbankRooms.reduce((acc, bedbankRoom) => {
      const isLoading = fetchingMap[bedbankRoom.propertyId]
      if (isLoading) return acc

      const propertyRates = ratesMap[bedbankRoom.propertyId]
      const matchingRate = propertyRates?.hotelOnlyRates.find(rate =>
        rate.id === bedbankRoom.rateId
      )

      if (!matchingRate) return acc

      const roomKey = `${bedbankRoom.propertyId}-${bedbankRoom.rateId}`

      if (!acc[roomKey]) {
        acc[roomKey] = { rate: matchingRate, count: 0 }
      }
      acc[roomKey].count++

      return acc
    }, {} as Record<string, { rate: App.BedbankRate; count: number }>)

    // Transform grouped rooms into display format
    return Object.values(roomDetails)
      .map(({ rate, count }) => {
        const roomSize = rate.room.features.find(f => f.id === 'dimension')?.text
        const bedConfiguration = rate.room.bedGroupsDescription

        // Early return if required fields are missing
        if (!roomSize || !bedConfiguration) return undefined

        return {
          name: `${count}x ${rate.room.name}`,
          size: roomSize,
          bedConfiguration,
        }
      }).filter(excludeNullOrUndefined) as Array<RoomDetails>
  }, [bedbankRooms, ratesMap, fetchingMap])

  return bedbankRoomsList
}

export default useBedbankRoomBreakdown
